import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import PlantSelection from './PlantSelection';
import Greenhouse from './Greenhouse';
import { plantData } from '../plantData';
import './OurPlant.css';

const OurPlant = () => {
    const [plant, setPlant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [plantId, setPlantId] = useState(null);
    const [showGreenhouse, setShowGreenhouse] = useState(false);

    // Fetch current user data
    useEffect(() => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setLoading(false);
            return;
        }
        const userDocRef = doc(db, 'users', currentUser.uid);
        const unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
            setUser(docSnap.exists() ? docSnap.data() : null);
        });
        return () => unsubscribeUser();
    }, []);

    // Determine shared plant ID
    useEffect(() => {
        if (user && user.partnerId) {
            const ids = [auth.currentUser.uid, user.partnerId].sort();
            setPlantId(ids.join('_'));
        } else {
            setPlantId(null);
        }
    }, [user]);

    // Listen to the shared plant document
    useEffect(() => {
        if (!plantId) {
            if (user && !user.partnerId) setLoading(false);
            return;
        }
        const plantDocRef = doc(db, 'ourPlants', plantId);
        const unsubscribePlant = onSnapshot(plantDocRef, (docSnap) => {
            setPlant(docSnap.exists() ? docSnap.data() : { completedPlants: [] });
            setLoading(false);
        });
        return () => unsubscribePlant();
    }, [plantId, user]);

    const handleSelectPlant = async (plantType) => {
        if (!plantId) return;
        const initialPlant = {
            plantType: plantType,
            growthPoints: 0,
            lastWatered: {},
        };
        const plantDocRef = doc(db, 'ourPlants', plantId);
        await setDoc(plantDocRef, initialPlant, { merge: true });
    };

    const handleWaterPlant = async () => {
        if (!plant || !plantId || !user) return;
        const uid = auth.currentUser.uid;
        const partnerId = user.partnerId;
        const currentPlantData = plantData[plant.plantType];
        const maxGrowth = currentPlantData.growthThresholds[currentPlantData.growthThresholds.length - 1];

        let pointsToAdd = 1; // Base points
        const partnerLastWatered = plant.lastWatered?.[partnerId];
        if (partnerLastWatered) {
            const partnerDate = new Date(partnerLastWatered.seconds * 1000);
            const today = new Date();
            if (partnerDate.toDateString() === today.toDateString()) {
                pointsToAdd = 3; // Synergy Boost!
            }
        }

        const newGrowthPoints = (plant.growthPoints || 0) + pointsToAdd;
        const plantDocRef = doc(db, 'ourPlants', plantId);

        if (newGrowthPoints >= maxGrowth) {
            // Plant is fully grown!
            await updateDoc(plantDocRef, {
                completedPlants: arrayUnion(plant.plantType),
                plantType: null, // Reset for new plant selection
                growthPoints: 0,
                lastWatered: {},
            });
        } else {
            await updateDoc(plantDocRef, {
                growthPoints: newGrowthPoints,
                [`lastWatered.${uid}`]: new Date(),
            });
        }
    };

    const getPlantImage = () => {
        const currentPlantData = plantData[plant.plantType];
        if (!currentPlantData) return '';
        const thresholds = currentPlantData.growthThresholds;
        let stage = 0;
        for (let i = thresholds.length - 1; i >= 0; i--) {
            if (plant.growthPoints >= thresholds[i]) {
                stage = i;
                break;
            }
        }
        return currentPlantData.images[stage];
    };

    const hasWateredToday = (uid) => {
        const lastWateredTimestamp = plant?.lastWatered?.[uid];
        if (!lastWateredTimestamp) return false;
        const lastDate = new Date(lastWateredTimestamp.seconds * 1000);
        return lastDate.toDateString() === new Date().toDateString();
    };

    if (loading) {
        return <div className="plant-container">Loading our plant...</div>;
    }
    if (!user?.partnerId) {
        return <div className="plant-container"><p>Please link with a partner to start a garden together.</p></div>;
    }

    if (showGreenhouse) {
        return <Greenhouse 
                    completedPlants={plant?.completedPlants || []} 
                    onBack={() => setShowGreenhouse(false)} 
                />;
    }

    if (!plant || !plant.plantType) {
        return (
            <div className="plant-container">
                <button onClick={() => setShowGreenhouse(true)} className="greenhouse-button top-right">
                    View Greenhouse
                </button>
                <PlantSelection onSelectPlant={handleSelectPlant} />
            </div>
        );
    }

    const canWater = !hasWateredToday(auth.currentUser.uid);

    return (
        <div className="plant-container">
            <div className="plant-display">
                <img src={getPlantImage()} alt={plant.plantType} className="plant-image" />
                <div className="water-status">
                    <p>You last watered: {plant.lastWatered?.[auth.currentUser.uid] ? new Date(plant.lastWatered[auth.currentUser.uid].seconds * 1000).toLocaleTimeString() : 'Never'}</p>
                    <p>Partner last watered: {plant.lastWatered?.[user.partnerId] ? new Date(plant.lastWatered[user.partnerId].seconds * 1000).toLocaleTimeString() : 'Never'}</p>
                </div>
            </div>
            <button onClick={handleWaterPlant} disabled={!canWater} className="water-button">
                {canWater ? 'Water Plant' : 'You watered today!'}
            </button>
            <br />
            <button onClick={() => setShowGreenhouse(true)} className="greenhouse-button">
                View Greenhouse
            </button>
        </div>
    );
};

export default OurPlant;